import { reportError } from "@/utils/reportError";
import {
  ACTIONS,
  CATEGORY_LABEL_KEYS,
  CATEGORY_ORDER,
  LOTO_BONUS_POINTS,
  getActionsByCategory,
} from "@/data/actions";
import { useDailyActions } from "@/hooks/useDailyActions";
import { useUserScores } from "@/hooks/useUserScores";
import { useContestEntry } from "@/hooks/useContestEntry";
import { useWheelSpin } from "@/hooks/useWheelSpin";
import {
  CHALLENGE_END,
  CHALLENGE_START,
  clampToChallenge,
  getDisplayToday,
  isLuckyWheelOpen,
  toRealDate,
} from "@/constants/challenge";
import { getTodayCH } from "@/utils/date";
import { computeBreakdown, isObjectiveMet } from "@/utils/scoring";
import { Ionicons } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActivityIndicator,
    Alert,
    ScrollView,
    StatusBar,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import ActionInfoModal from "./ActionInfoModal";
import DailyIntroModal from "./DailyIntroModal";
import DateSelector from "./DateSelector";
import LuckyWheelModal from "./LuckyWheelModal";
import TrackableActionCard from "./TrackableActionCard";
import WeeklyRecap from "./WeeklyRecap";

const DAILY_INTRO_SEEN_KEY = "daily_intro_seen_v1";
// Last day the contest wheel was auto-proposed. Lets us re-prompt once per day
// while the user hasn't entered yet — i.e. "reproposer la pop-up si refus" (#9).
const WHEEL_PROMPT_DATE_KEY = "wheel_prompt_date_v1";

interface ActionTrackingPageProps {
  selectedActionIds: number[];
}

type ActionInfo = {
  nameKey: string;
  descriptionKey: string;
  infoUrl?: string;
  infoLabelKey?: string;
};

export default function ActionTrackingPage({
  selectedActionIds,
}: ActionTrackingPageProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<"daily" | "weekly">("daily");
  // Jour affiché, 'YYYY-MM-DD' (jour suisse) — borné à la fenêtre du challenge.
  // `selectedDate` = jour AFFICHÉ (calendrier du concours). En mode test il est
  // mappé sur le jour réel pour les requêtes/écritures via toRealDate().
  const [selectedDate, setSelectedDate] = useState(clampToChallenge(getDisplayToday()));
  const { todayActions, saveActions, loading: dailyActionsLoading, refetch: refetchDailyActions } = useDailyActions(toRealDate(selectedDate));
  // La validation n'est possible que pour le jour suisse courant : consulter
  // un autre jour du challenge reste en lecture seule (le serveur force de
  // toute façon action_date = aujourd'hui).
  const isViewingToday = selectedDate === getDisplayToday();
  const { scores, updateStreak } = useUserScores();
  const streakDays = scores?.streakDays ?? 0;
  const { hasEntered, loading: contestLoading, refetch: refetchContestEntry } = useContestEntry();
  // `spunToday` pilote le point de notif de la roue : il disparaît une fois le
  // tour du jour joué et réapparaît le lendemain. La roue, elle, reste toujours
  // accessible (le bouton n'est jamais masqué).
  const { spunToday, refetch: refetchSpin } = useWheelSpin();
  const [pendingSelections, setPendingSelections] = useState<Record<number, boolean>>({});
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [wheelModalVisible, setWheelModalVisible] = useState(false);
  const [introModalVisible, setIntroModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Garde synchrone anti double-tap : `isSaving` (state) n'est mis à jour qu'au
  // re-render, donc deux taps rapides le voient à false et créditent les points
  // 2× (inflation permanente, user_scores non recalculé serveur). Le ref bloque
  // la 2e exécution immédiatement.
  const savingRef = useRef(false);
  const [selectedInfo, setSelectedInfo] = useState<ActionInfo | null>(null);

  useEffect(() => {
    checkDailyIntro();
    refetchDailyActions();
    refetchContestEntry();
  }, []);

  // Re-propose the contest wheel once per day until the user has entered (#9).
  // Gated on the daily intro already being seen so two modals never stack.
  useEffect(() => {
    if (contestLoading || hasEntered || !isLuckyWheelOpen()) return;
    let cancelled = false;
    (async () => {
      try {
        const introSeen = await AsyncStorage.getItem(DAILY_INTRO_SEEN_KEY);
        if (introSeen !== "true") return;
        const today = getTodayCH();
        const lastPrompt = await AsyncStorage.getItem(WHEEL_PROMPT_DATE_KEY);
        if (lastPrompt === today) return;
        await AsyncStorage.setItem(WHEEL_PROMPT_DATE_KEY, today);
        if (!cancelled) setWheelModalVisible(true);
      } catch {
        // non-blocking
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contestLoading, hasEntered]);

  const checkDailyIntro = async () => {
    try {
      const seen = await AsyncStorage.getItem(DAILY_INTRO_SEEN_KEY);
      if (seen !== "true") {
        setIntroModalVisible(true);
      }
    } catch {
      // ignore; non-blocking
    }
  };

  const dismissIntroModal = async () => {
    setIntroModalVisible(false);
    try {
      await AsyncStorage.setItem(DAILY_INTRO_SEEN_KEY, "true");
    } catch {
      // ignore
    }
  };

  // Plage forcée sur la fenêtre du challenge (14–25 septembre). Équivalent
  // i18n de getChallengeRangeLabel() (constants/challenge.ts, non modifiable) :
  // libellé construit via les clés `dates.*` pour l'affichage FR/EN.
  const getWeekRange = () => {
    const [, startMonth, startDay] = CHALLENGE_START.split("-").map(Number);
    const [, endMonth, endDay] = CHALLENGE_END.split("-").map(Number);
    return startMonth === endMonth
      ? t("dates.challengeRangeSameMonth", {
          startDay,
          endDay,
          month: t(`dates.month${endMonth}`),
        })
      : t("dates.challengeRangeTwoMonths", {
          startDay,
          startMonth: t(`dates.month${startMonth}`),
          endDay,
          endMonth: t(`dates.month${endMonth}`),
        });
  };

  const alreadyDoneIds = useMemo(
    () => new Set(todayActions.map((a) => a.actionId)),
    [todayActions]
  );

  const toggleAction = (actionId: number) => {
    if (alreadyDoneIds.has(actionId)) return;
    setPendingSelections((prev) => {
      const next = { ...prev };
      if (next[actionId]) {
        delete next[actionId];
      } else {
        next[actionId] = true;
      }
      return next;
    });
  };

  const showInfo = (action: (typeof ACTIONS)[0]) => {
    setSelectedInfo({
      nameKey: action.nameKey,
      descriptionKey: action.descriptionKey,
      infoUrl: action.infoUrl,
      infoLabelKey: action.infoLabelKey,
    });
    setInfoModalVisible(true);
  };

  const completedTodayIds = useMemo(() => {
    const pending = Object.keys(pendingSelections).map(Number);
    return Array.from(new Set([...alreadyDoneIds, ...pending]));
  }, [alreadyDoneIds, pendingSelections]);

  // Estimation LOCALE pour le libellé du bouton Valider uniquement (aperçu
  // « +X pts » avant validation). Le crédit réel est fait et renvoyé par le
  // serveur (RPC validate_daily_actions) au moment de valider.
  const { actionPoints, lotoBonus, totalPoints } = useMemo(() => {
    const pendingIds = Object.keys(pendingSelections).map(Number);
    return computeBreakdown(ACTIONS, Array.from(alreadyDoneIds), pendingIds);
  }, [pendingSelections, alreadyDoneIds]);
  const hasSelectedActions = Object.keys(pendingSelections).length > 0;

  const groupedActions = useMemo(() => getActionsByCategory(), []);

  const handleValidate = async () => {
    if (!hasSelectedActions || isSaving || savingRef.current || !isViewingToday) return;
    // Garde conservée : on ne valide pas tant que l'état du jour n'est pas
    // chargé (évite un aperçu de points erroné dans le bouton). Le crédit
    // lui-même est désormais idempotent côté serveur, donc un état local
    // périmé ne peut plus provoquer de sur-crédit.
    if (dailyActionsLoading) return;
    // Verrou synchrone posé AVANT tout await (cf. savingRef plus haut).
    savingRef.current = true;

    const completedActionIds = Object.keys(pendingSelections).map(Number);
    const objectiveMet = isObjectiveMet(
      alreadyDoneIds.size,
      completedActionIds.length,
      selectedActionIds.length,
    );

    setPendingSelections({});
    setIsSaving(true);

    // Crédit 100 % SERVEUR (RPC validate_daily_actions) : le serveur déduplique,
    // lit les points depuis `actions`, calcule le bonus ligne +50 et crédite
    // user_scores atomiquement (total/weekly/monthly + bonus_points). Le client
    // ne crédite PLUS rien lui-même → fin du trou de sur-crédit (état local
    // périmé qui re-créditait actions/bonus). Seul updateStreak (transition de
    // streak, indépendante) reste côté client.
    try {
      const result = await saveActions(completedActionIds);
      if (!result.success) {
        Alert.alert(t('home.saveFailedTitle'), t('home.saveFailedMessage'));
        return;
      }

      // L'écran de félicitations montre le RÉCAP DU JOUR (toutes les actions
      // validées aujourd'hui), pas seulement le dernier lot soumis — sinon les
      // « Résultats » se remettaient à zéro à chaque soumission (ex. valider la
      // ligne zéro déchet après coup affichait 90 / 0 / 0 alors que les 9
      // actions du jour étaient faites). On agrège donc les actions déjà faites
      // + celles qui viennent d'être créditées, et on recalcule le total, le
      // bonus lignes et la ventilation par catégorie sur cet ensemble.
      // (Affichage uniquement — le crédit réel du score reste 100 % serveur.)
      const dayActionIds = Array.from(
        new Set([...alreadyDoneIds, ...result.creditedActionIds])
      );
      const dayBreakdown = computeBreakdown(ACTIONS, [], dayActionIds);
      const navParams = {
        totalPoints: dayBreakdown.totalPoints.toString(),
        actionPoints: dayBreakdown.actionPoints.toString(),
        bonusPoints: dayBreakdown.lotoBonus.toString(),
        zeroWastePoints: dayBreakdown.zeroWastePoints.toString(),
        balancedFoodPoints: dayBreakdown.balancedFoodPoints.toString(),
        localFoodPoints: dayBreakdown.localFoodPoints.toString(),
        selectedActionsCount: dayActionIds.length.toString(),
        actionsToday: result.actionsToday.toString(),
        allActionsDone: result.allDone ? '1' : '0',
      };

      await updateStreak(objectiveMet);

      router.push({ pathname: '/celebration', params: navParams });
    } catch (error) {
      reportError(error);
      Alert.alert(t('home.saveFailedTitle'), t('home.saveFailedMessage'));
    } finally {
      setIsSaving(false);
      savingRef.current = false;
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#FBEFE3" }} edges={["top"]}>
      <StatusBar barStyle="dark-content" backgroundColor="#FBEFE3" />
      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 1]}
        style={styles.container}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.tabsContainer}>
            <TouchableOpacity
              style={styles.tab}
              onPress={() => setActiveTab("daily")}
            >
              {activeTab === "daily" ? (
                <LinearGradient
                  colors={["#CD7728", "#FCC189"]}
                  locations={[0, 0.41]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.tabActiveGradient}
                >
                  <Text style={[styles.tabText, styles.tabTextActive]}>{t("home.tabDaily")}</Text>
                </LinearGradient>
              ) : (
                <Text style={styles.tabText}>{t("home.tabDaily")}</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.tab}
              onPress={() => setActiveTab("weekly")}
            >
              {activeTab === "weekly" ? (
                <LinearGradient
                  colors={["#FCC189", "#CD7728"]}
                  locations={[0, 0.41]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.tabActiveGradient}
                >
                  <Text style={[styles.tabText, styles.tabTextActive]}>{t("home.tabWeekly")}</Text>
                </LinearGradient>
              ) : (
                <Text style={styles.tabText}>{t("home.tabWeekly")}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.header}>
            <View style={styles.titleColumn}>
              <Text style={styles.title}>{t("home.title")}</Text>
              {streakDays >= 2 && (
                <View
                  style={styles.streakBadge}
                  accessibilityLabel={t("home.streakA11y", { count: streakDays })}
                >
                  <Text style={styles.streakEmoji}>🔥</Text>
                  <Text style={styles.streakText}>
                    {t("home.streakDays", { count: streakDays })}
                  </Text>
                </View>
              )}
            </View>
            {activeTab === "weekly" ? (
              <Text style={styles.dateRange}>{getWeekRange()}</Text>
            ) : (
              <DateSelector
                selectedDate={selectedDate}
                onDateChange={(d) => {
                  // Réinitialise les sélections non validées : elles ne doivent
                  // pas « suivre » sur un autre jour (faux cochés en naviguant).
                  setPendingSelections({});
                  setSelectedDate(d);
                }}
              />
            )}
          </View>

          {activeTab === "weekly" ? (
            // Vue « Semaine » = récapitulatif LECTURE SEULE du challenge :
            // ni grille cochable, ni bouton Valider.
            <WeeklyRecap />
          ) : (
            <>
          {CATEGORY_ORDER.map((category) => {
            const actions = groupedActions[category] ?? [];
            const idsInCat = actions.map((a) => a.id);
            const lineComplete = idsInCat.every((id) => completedTodayIds.includes(id));
            return (
              <View key={category} style={styles.categorySection}>
                <View style={styles.categoryHeader}>
                  <Text style={styles.categoryTitle}>{t(CATEGORY_LABEL_KEYS[category])}</Text>
                  {lineComplete && (
                    <View style={styles.lineBadge}>
                      <Ionicons name="trophy" size={12} color="#FFF" />
                      <Text style={styles.lineBadgeText}>
                        {t("common.plusPointsShort", { points: LOTO_BONUS_POINTS })}
                      </Text>
                    </View>
                  )}
                </View>
                <View style={styles.actionsGrid}>
                  {actions.map((action) => (
                    <TrackableActionCard
                      key={action.id}
                      id={action.id}
                      name={t(action.nameKey)}
                      icon={action.icon}
                      description={t(action.descriptionKey)}
                      points={action.points}
                      count={pendingSelections[action.id] ? 1 : 0}
                      alreadyDoneToday={alreadyDoneIds.has(action.id)}
                      onPress={() => toggleAction(action.id)}
                      onInfoPress={() => showInfo(action)}
                    />
                  ))}
                </View>
              </View>
            );
          })}

          <TouchableOpacity
            style={styles.validateButton}
            onPress={handleValidate}
            disabled={!hasSelectedActions || isSaving || !isViewingToday || dailyActionsLoading}
          >
            <LinearGradient
              colors={
                hasSelectedActions && !isSaving && isViewingToday && !dailyActionsLoading
                  ? ["#1C453F", "#78A584"]
                  : ["#CCCCCC", "#999999"]
              }
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.validateGradient}
            >
              {isSaving ? (
                <ActivityIndicator size="small" color="#FFF" />
              ) : (
                <Text style={styles.validateText}>
                  {!isViewingToday
                    ? t('home.validateNotToday')
                    : hasSelectedActions
                      ? lotoBonus > 0
                        ? t('home.validateWithBonus', {
                            actionPoints,
                            bonus: lotoBonus,
                            total: totalPoints,
                          })
                        : t('home.validateWithPoints', { points: totalPoints })
                      : t('common.validate')}
                </Text>
              )}
            </LinearGradient>
          </TouchableOpacity>
            </>
          )}
        </ScrollView>

        <ActionInfoModal
          visible={infoModalVisible}
          title={selectedInfo ? t(selectedInfo.nameKey) : null}
          description={selectedInfo ? t(selectedInfo.descriptionKey) : null}
          infoUrl={selectedInfo?.infoUrl}
          infoLabel={selectedInfo?.infoLabelKey ? t(selectedInfo.infoLabelKey) : undefined}
          onClose={() => setInfoModalVisible(false)}
        />

        <LuckyWheelModal
          visible={wheelModalVisible}
          onClose={() => {
            setWheelModalVisible(false);
            // Rafraîchit l'état du jour pour masquer le point de notif dès que
            // le tour a été joué.
            refetchSpin();
            refetchContestEntry();
          }}
        />

        <DailyIntroModal
          visible={introModalVisible}
          onClose={dismissIntroModal}
        />

        {/* La roue reste toujours accessible. Seul le point de notif dépend du
            tour du jour : présent tant qu'il n'a pas été joué. */}
        <TouchableOpacity
          style={styles.floatingWheelButton}
          onPress={() => setWheelModalVisible(true)}
          activeOpacity={0.8}
        >
          <LinearGradient
            colors={["#CD7728", "#FCC189"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.floatingWheelGradient}
          >
            <Ionicons name="gift" size={24} color="#FFF" />
            {/* Le tour du jour est « fait » soit par des points gagnés
                (spunToday → wheel_spins), soit par un cadeau qualifié
                (hasEntered → contest_entries). Dans les deux cas le point de
                notif disparaît jusqu'au lendemain. */}
            {isLuckyWheelOpen() && !spunToday && !hasEntered && <View style={styles.notificationDot} />}
          </LinearGradient>
        </TouchableOpacity>
      </LinearGradient>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 24,
  },
  scrollContent: {
    padding: 24,
    paddingTop: 0,
    paddingBottom: 24,
  },
  tabsContainer: {
    flexDirection: "row",
    backgroundColor: "#FFF",
    borderRadius: 12,
    marginBottom: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  tab: {
    flex: 1,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    padding: 6,
  },
  tabActiveGradient: {
    flex: 1,
    width: "100%",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  tabText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
  },
  tabTextActive: {
    padding: 6,
    color: "#FFF",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  titleColumn: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 6,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 28,
    color: "#000",
  },
  streakBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "#FFE8D6",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  streakEmoji: {
    fontSize: 14,
  },
  streakText: {
    fontFamily: "Inter_700Bold",
    fontSize: 12,
    color: "#CD7728",
  },
  dateRange: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
  },
  categorySection: {
    marginBottom: 12,
  },
  categoryHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  categoryTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#000",
  },
  lineBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "#83BF4F",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  lineBadgeText: {
    fontFamily: "Inter_700Bold",
    fontSize: 10,
    color: "#FFF",
  },
  actionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  validateButton: {
    marginTop: 6,
  },
  validateGradient: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
  },
  validateText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFF",
    textAlign: "center",
  },
  floatingWheelButton: {
    position: "absolute",
    bottom: 100,
    right: 20,
    borderRadius: 28,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  floatingWheelGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: "center",
    justifyContent: "center",
  },
  notificationDot: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: "#FF3B30",
    borderWidth: 2,
    borderColor: "#FFF",
  },
});
